import { useCallback, useMemo, useState } from 'react';

import { Search as SearchIcon } from '@mui/icons-material';
import { Box, Divider, Paper, TextField, Typography } from '@mui/material';
import ExcelJS from 'exceljs';
import { MLDataGrid } from 'ml-react-component-library';
import { useDebouncedCallback } from 'use-debounce';

import { usePaginatedWaiverDocuments } from '@/apps/engagement-letter-database/hooks/usePaginatedWaiverDocuments';
import type { ParsedIManageDocument } from '@/apps/engagement-letter-database/hooks/useParseRecords';
import type { ParsedWaiverRecord } from '@/apps/engagement-letter-database/store/useWaiverRecord';
import { buildIManageUrl } from '@/shared/helpers/imanageUrlUtils';

import CustomToolbar from '../../../../Records/Dashboard/components/SearchResults/CustomToolbar';
import { createWaiverDocColumns } from '../../constants';

const DEBOUNCE_MS = 300;

const SearchResults = ({ record: waiverRecord }: { record: ParsedWaiverRecord }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const debouncedSetSearch = useDebouncedCallback((value: string) => setDebouncedSearchTerm(value), DEBOUNCE_MS);
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSetSearch(value);
  };

  const {
    rows,
    rowCount,
    paginationModel,
    setPaginationModel,
    loading: docsLoading,
  } = usePaginatedWaiverDocuments(waiverRecord.id, debouncedSearchTerm);

  const documentRows = useMemo<ParsedIManageDocument[]>(
    () =>
      rows.map((row) => ({
        ...row,
        id: row.docId,
        docId: row.docId,
        iManageDocNumber: row.docId,
        documentName: row.docName ?? row.docId,
      })),
    [rows],
  );

  const columns = createWaiverDocColumns();

  const handleExport = useCallback(async () => {
    const workbook = new ExcelJS.Workbook();
    const documentsWorksheet = workbook.addWorksheet('Waiver Letter Documents');
    const waiverInfoWorksheet = workbook.addWorksheet('Waiver Info');

    waiverInfoWorksheet.columns = [
      { header: 'Client Name', key: 'clientName', width: 30 },
      { header: 'Client Number', key: 'clientNumber', width: 20 },
      { header: 'Matter Number', key: 'matterNumber', width: 20 },
      { header: 'Client Contact', key: 'clientContact', width: 25 },
      { header: 'Client Letter Date', key: 'clientLetterDate', width: 18 },
      { header: 'Client MLB Attorney', key: 'clientAttorney', width: 30 },
      { header: 'Adverse Client Name', key: 'adverseClientName', width: 30 },
      { header: 'Adverse Contact Signer', key: 'adverseContactSigner', width: 25 },
      { header: 'Adverse Letter Date', key: 'adverseLetterDate', width: 18 },
      { header: 'Adverse MLB Attorney', key: 'adverseAttorney', width: 30 },
      { header: 'Multiple Waivers', key: 'multipleWaivers', width: 18 },
      { header: 'Comments', key: 'comments', width: 50 },
      { header: 'Created By', key: 'createdBy', width: 25 },
      { header: 'Created Date', key: 'createdDate', width: 20 },
      { header: 'Modified By', key: 'modifiedBy', width: 25 },
      { header: 'Modified Date', key: 'modifiedDate', width: 20 },
    ];
    waiverInfoWorksheet.getRow(1).font = { bold: true };
    waiverInfoWorksheet.addRow({
      clientName: waiverRecord.clientName || 'N/A',
      clientNumber: waiverRecord.clientNumber || 'N/A',
      matterNumber: waiverRecord.matterNumber || 'N/A',
      clientContact: waiverRecord.clientContact || 'N/A',
      clientLetterDate: waiverRecord.clientLetterDate || 'N/A',
      clientAttorney: waiverRecord.clientAttorney || 'N/A',
      adverseClientName: waiverRecord.adverseClientName || 'N/A',
      adverseContactSigner: waiverRecord.adverseContactSigner || 'N/A',
      adverseLetterDate: waiverRecord.adverseLetterDate || 'N/A',
      adverseAttorney: waiverRecord.adverseAttorney || 'N/A',
      multipleWaivers: waiverRecord.multipleWaivers || 'N/A',
      comments: waiverRecord.comments || '',
      createdBy: waiverRecord.createdByUser?.displayName || waiverRecord.createdBy || 'N/A',
      createdDate: waiverRecord.createdAt?.format('YYYY-MM-DD') ?? '',
      modifiedBy: waiverRecord.updatedByUser?.displayName || waiverRecord.updatedBy || 'N/A',
      modifiedDate: waiverRecord.updatedAt?.format('YYYY-MM-DD') ?? '',
    });

    documentsWorksheet.columns = [
      { header: 'Document Name', key: 'documentName', width: 40 },
      { header: 'iManage Doc Number', key: 'iManageDocNumber', width: 24 },
      { header: 'iManage Link', key: 'iManageDocLink', width: 50 },
    ];
    documentsWorksheet.getRow(1).font = { bold: true };

    documentRows.forEach((row) => {
      documentsWorksheet.addRow({
        documentName: row.docName ?? '',
        iManageDocNumber: row.docId,
        iManageDocLink: buildIManageUrl(row.docId),
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const namePart =
      (waiverRecord.clientName || waiverRecord.adverseClientName)
        ?.trim()
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '') || 'waiver-documents';
    link.download = `${namePart}-${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }, [documentRows, waiverRecord]);

  return (
    <Paper elevation={2} sx={{ p: 3, marginBottom: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Waiver Letter Documents
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ paddingTop: 2, paddingBottom: 1 }}>
          <TextField
            sx={{
              width: 325,
              maxWidth: '100%',
            }}
            label="Search Documents"
            placeholder="Search by document name or doc number..."
            size="small"
            variant="outlined"
            margin="none"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', marginRight: 1 }}>
                    <SearchIcon />
                  </Box>
                ),
              },
            }}
          />
        </Box>
      </Box>
      <MLDataGrid
        showToolbar
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: { onExport: () => void handleExport() },
        }}
        rows={documentRows}
        columns={columns}
        paginationMode="server"
        pagination
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[25, 50, 100]}
        loading={docsLoading}
      />
    </Paper>
  );
};

export default SearchResults;
